import { SupportedMeasureType } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { ChartFormFieldsEnum, FilterFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { extractRatioParts, extractRetentionEventName } from "../extract-ratio-parts";
import dayjs from "dayjs";
import { ALL_EXTRA_FIELDS_FILTER, DEFAULT_SIGNIFICANCE_LEVEL } from "@src/pages/ab-test-explore/contanst";
import { convertDateRangeToDayJS } from "../time-form";
import { DEFAULT_TITLE } from "@src/pages/ab-test-explore/edit";
import { MEASURE_RETENTION_BY_EVENT_NAME } from "@src/constant/ab-testing";
import { reStructure } from "./funnel/manage-state-event-lists";
import { FormInstance } from "antd/lib";

const getValueFromTemplate = (measureOption: any, measureValue: string) => {
    const regex = measureOption?.matched_regex;
    if (!regex) return {};

    if (measureOption?.[ChartFormFieldsEnum.METRIC_MEASURE_TYPE] === SupportedMeasureType.RATIO) {
        const matched = extractRatioParts(measureValue, regex) ?? {
            numerator: null,
            denominator: null,
        };

        return {
            [ChartFormFieldsEnum.NUMERATOR]: matched.numerator,
            [ChartFormFieldsEnum.DENOMINATOR]: matched.denominator,
        };
    }
    if (measureOption?.name === MEASURE_RETENTION_BY_EVENT_NAME) {
        const matched = extractRetentionEventName(measureValue, regex) ?? {
            event_name: null,
        };
        return {
            [ChartFormFieldsEnum.EVENT_NAME]: matched?.event_name,
        };
    }
    return {};
};

export const parsedChartFormValueFromApi = (values: Record<string, any>) => {
    const metricTabItems: any[] = values?.[ChartFormFieldsEnum.METRIC_TAB_ITEMS] ?? [];

    const metricPerformanceDetail = metricTabItems.reduce<Record<string, any>>((acc, item) => {
        const rawOptions = item?.[ChartFormFieldsEnum.METRIC_MEASURE_OPTIONS];
        const measureValue = item?.[ChartFormFieldsEnum.METRIC_MEASURE_VALUE];

        const parsedOptions = rawOptions ? JSON.parse(rawOptions) : {};
        const extractedValues = getValueFromTemplate(parsedOptions, measureValue);

        acc[item.key] = {
            ...item,
            ...extractedValues,
        };

        return acc;
    }, {});

    const formattedFields = {
        [ChartFormFieldsEnum.CUSTOM_TIME_RANGE]: (values?.[ChartFormFieldsEnum.CUSTOM_TIME_RANGE] ?? []).map(
            (item: string) => dayjs(item),
        ),
        [ChartFormFieldsEnum.FUNNEL_EVENT_LISTS]:
            typeof values?.[ChartFormFieldsEnum.FUNNEL_EVENT_LISTS] === "string"
                ? reStructure(JSON.parse(values?.[ChartFormFieldsEnum.FUNNEL_EVENT_LISTS] ?? []))
                : values?.[ChartFormFieldsEnum.FUNNEL_EVENT_LISTS],
    };

    return {
        [ChartFormFieldsEnum.FUNNEL_SIGNIFICANT]: DEFAULT_SIGNIFICANCE_LEVEL,
        [ChartFormFieldsEnum.RETENTION_SIGNIFICANT]: DEFAULT_SIGNIFICANCE_LEVEL,
        ...values,
        ...metricPerformanceDetail,
        ...formattedFields,
    };
};

export const parsedFilterFormValueFromApi = (
    values: Record<string, any>,
    syncExtraConfirm?: {
        form: FormInstance;
    },
) => {
    const {
        [FilterFormFieldsEnum.VARIANTS]: variants,
        [FilterFormFieldsEnum.BASE_VARIANTS]: baseVariants,
        [FilterFormFieldsEnum.TIME_RANGE]: timeRange,
        [FilterFormFieldsEnum.VARIANTS_OPTIONS]: variantOptions,
        [FilterFormFieldsEnum.EXTRA_INSTALL_DATE]: installDate,
        [FilterFormFieldsEnum.EXTRA_ONBOARDING_DATE]: onboardingDate,
        ...rest
    } = values;

    const sortedVariants = variants?.sort() ?? undefined;
    const sortedVariantOptions = variantOptions?.sort((a: any, b: any) => a.value?.localeCompare(b.value)) ?? undefined;

    const defaultFilterFields = Object.fromEntries(
        Object.entries({
            [FilterFormFieldsEnum.TITLE]: DEFAULT_TITLE,
            [FilterFormFieldsEnum.DESCRIPTION]: "",
            [FilterFormFieldsEnum.VARIANTS]: sortedVariants,
            [FilterFormFieldsEnum.EXTRA_VARIANTS]: sortedVariants,
            [FilterFormFieldsEnum.BASE_VARIANTS]: baseVariants,
            [FilterFormFieldsEnum.TIME_RANGE]: timeRange
                ? convertDateRangeToDayJS(timeRange)
                : [dayjs().add(-14, "d"), dayjs()],
            [FilterFormFieldsEnum.VARIANTS_OPTIONS]: sortedVariantOptions,
            [FilterFormFieldsEnum.IS_ABA_ID_CHANGED]: false,
            [FilterFormFieldsEnum.EXTRA_INSTALL_DATE]: installDate,
            [FilterFormFieldsEnum.EXTRA_ONBOARDING_DATE]: onboardingDate,
            ...rest,
        }).filter(([_, value]) => value !== undefined),
    );

    const extraConfirmedFields: Record<string, any> = {};
    ALL_EXTRA_FIELDS_FILTER.forEach((field) => {
        const val = defaultFilterFields[field];
        if (val) extraConfirmedFields[field] = val;
    });

    const userFilterDefaults = {
        [FilterFormFieldsEnum.INVALID_COUNTRIES]: true,
        [FilterFormFieldsEnum.MULTI_USER_PSEUDO_ID]: true,
        [FilterFormFieldsEnum.USERS_JOIN_MULTIPLE_GROUPS]: true,
        [FilterFormFieldsEnum.LATE_ONBOARDING_DATE]: true,
    };

    return {
        ...userFilterDefaults,
        ...defaultFilterFields,
        [FilterFormFieldsEnum.EXTRA_FILTER_VALUES_CONFIRMED]: JSON.stringify(
            getNewExtraFilterConfirmed(
                {
                    ...userFilterDefaults,
                    ...extraConfirmedFields,
                },
                syncExtraConfirm?.form,
            ),
        ),
        [FilterFormFieldsEnum.IS_SHOW_CHIP]: true,
        [FilterFormFieldsEnum.EXTRA_INSTALL_DATE]: convertDateRangeToDayJS(installDate),
        [FilterFormFieldsEnum.EXTRA_ONBOARDING_DATE]: convertDateRangeToDayJS(onboardingDate),
    };
};

const getNewExtraFilterConfirmed = (newValues: Record<string, any>, form?: FormInstance): Record<string, any> => {
    if (!form) return newValues;
    try {
        return {
            [FilterFormFieldsEnum.EXTRA_PLATFORM]: form.getFieldValue(FilterFormFieldsEnum.PLATFORM),
            [FilterFormFieldsEnum.EXTRA_VARIANTS]: form.getFieldValue(FilterFormFieldsEnum.VARIANTS),
            ...newValues,
        };
    } catch (error) {
        return newValues;
    }
};
